import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StudentAvatar } from "@/components/StudentAvatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GraduationCap, Plus, Search, Loader2, Trash2, UserPlus, Eye, Pencil, FileSpreadsheet, FileDown, Upload, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { PriorGradesDialog } from "@/components/PriorGradesDialog";

const emptyGuardian = { name: "", relationship: "", phone: "", whatsapp: "", email: "", is_primary: true, is_legal: true };
const fmtCpf = (v) => (v || "").replace(/\D/g, "").slice(0, 11).replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");

export default function Students() {
  const { scopeParams, selectedSchoolId, isSuperadmin, user } = useAuth();
  const [students, setStudents] = useState(null);
  const [classes, setClasses] = useState([]);
  const [years, setYears] = useState([]);
  const [q, setQ] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [gradesIdx, setGradesIdx] = useState(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const canEdit = ["SUPERADMIN", "DIRECAO", "COORDENACAO"].includes(user.role);

  useEffect(() => { if (params.get("class")) setClassFilter(params.get("class")); }, [params]);

  const fetchStudents = useCallback(() => {
    const p = { ...scopeParams };
    if (q) p.q = q;
    if (classFilter !== "all") p.class_id = classFilter;
    api.get("/students", { params: p }).then((r) => setStudents(r.data));
  }, [scopeParams, q, classFilter]);

  useEffect(() => {
    api.get("/classes", { params: scopeParams }).then((r) => setClasses(r.data));
    api.get("/academic-years", { params: scopeParams }).then((r) => setYears(r.data));
    // eslint-disable-next-line
  }, [selectedSchoolId]);

  useEffect(() => { const t = setTimeout(fetchStudents, 250); return () => clearTimeout(t); }, [fetchStudents]);

  const openNew = () => { setEditing(null); setForm({ full_name: "", registration: "", birth_date: "", class_id: "", academic_year_id: years[0]?.id || "", enrollment_status: "MATRICULADO", address: "", photo_url: "", guardians: [{ ...emptyGuardian }] }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s, guardians: s.guardians?.length ? s.guardians : [{ ...emptyGuardian }] }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setG = (i, k, v) => setForm((f) => { const g = [...f.guardians]; g[i] = { ...g[i], [k]: v }; return { ...f, guardians: g }; });
  const addG = () => setForm((f) => ({ ...f, guardians: [...f.guardians, { ...emptyGuardian, is_primary: false, is_legal: false }] }));
  const removeG = (i) => setForm((f) => ({ ...f, guardians: f.guardians.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, guardians: form.guardians.filter((g) => g.name) };
      if (editing) await api.put(`/students/${editing.id}`, payload);
      else await api.post("/students", payload);
      toast.success(editing ? "Estudante atualizado" : "Estudante cadastrado"); setOpen(false); fetchStudents();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const [impOpen, setImpOpen] = useState(false);
  const [impStep, setImpStep] = useState(1);
  const [impFile, setImpFile] = useState(null);
  const [impReport, setImpReport] = useState([]);
  const [impSummary, setImpSummary] = useState(null);
  const [impJob, setImpJob] = useState(null);
  const [impResults, setImpResults] = useState([]);
  const [impBusy, setImpBusy] = useState(false);

  const openImport = () => { setImpStep(1); setImpFile(null); setImpReport([]); setImpSummary(null); setImpJob(null); setImpResults([]); setImpOpen(true); };
  const downloadTemplate = async () => {
    try {
      const r = await api.get("/students/import/template", { params: scopeParams, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "modelo_estudantes.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao baixar o modelo"); }
  };
  const doPreview = async () => {
    if (!impFile) return; setImpBusy(true);
    try {
      const fd = new FormData(); fd.append("file", impFile);
      if (scopeParams.school_id) fd.append("school_id", scopeParams.school_id);
      const r = await api.post("/students/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImpJob(r.data.id); setImpReport(r.data.report); setImpSummary(r.data.summary); setImpStep(2);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setImpBusy(false); }
  };
  const doConfirm = async () => {
    setImpBusy(true);
    try {
      const r = await api.post(`/students/import/${impJob}/confirm`);
      setImpResults(r.data.results); setImpSummary(r.data.summary); setImpStep(3);
      toast.success(`${r.data.summary.created} estudante(s) importado(s)`); fetchStudents();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setImpBusy(false); }
  };

  const [delStudent, setDelStudent] = useState(null);
  const [deletingStu, setDeletingStu] = useState(false);
  const doDeleteStudent = async () => {
    setDeletingStu(true);
    try { await api.delete(`/students/${delStudent.id}`); toast.success("Estudante excluído"); setDelStudent(null); fetchStudents(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeletingStu(false); }
  };

  if (students === null) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;
  const needsSchool = isSuperadmin && !selectedSchoolId;
  return (
    <div>
      <PageHeader title="Estudantes" subtitle="Cadastro único do estudante — o centro da plataforma">
        {canEdit && <Button onClick={openImport} disabled={needsSchool} variant="outline" data-testid="student-import-button"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Importar planilha</Button>}
        {canEdit && <Button onClick={openNew} disabled={needsSchool} data-testid="student-add-button" className="bg-blue-700 hover:bg-blue-800"><Plus className="mr-1.5 h-4 w-4" /> Novo Estudante</Button>}
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="students-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou matrícula" className="pl-9" />
        </div>
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="students-filter-class"><SelectValue placeholder="Turma" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as turmas</SelectItem>
            {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.grade}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {students.length === 0 ? <EmptyState icon={GraduationCap} title="Nenhum estudante encontrado" description="Ajuste a busca ou cadastre um novo estudante." /> : (
        <Card className="overflow-x-auto border-slate-200" data-testid="students-table">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Estudante</TableHead><TableHead>Matrícula</TableHead><TableHead>Turma</TableHead>
              <TableHead>Situação</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {students.map((s) => (
                <TableRow key={s.id} data-testid={`student-row-${s.id}`} className="cursor-pointer" onClick={() => navigate(`/alunos/${s.id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <StudentAvatar student={s} className="h-9 w-9" fallbackClassName="bg-blue-100 text-xs text-blue-700" />
                      <span className="font-medium text-slate-800">{s.full_name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-slate-500">{s.registration}</TableCell>
                  <TableCell className="text-slate-600">{s.class_name || "—"}</TableCell>
                  <TableCell><StatusBadge status={s.enrollment_status} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" data-testid={`student-profile-link-${s.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/alunos/${s.id}`); }}><Eye className="h-4 w-4" /></Button>
                    {canEdit && <Button variant="ghost" size="sm" data-testid={`student-edit-${s.id}`} onClick={(e) => { e.stopPropagation(); openEdit(s); }}><Pencil className="h-4 w-4" /></Button>}
                    {canEdit && <Button variant="ghost" size="sm" data-testid={`student-delete-${s.id}`} onClick={(e) => { e.stopPropagation(); setDelStudent(s); }} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Editar Estudante" : "Novo Estudante"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label>Nome completo *</Label><Input data-testid="student-name-input" value={form.full_name || ""} onChange={(e) => set("full_name", e.target.value)} /></div>
              <div><Label>Matrícula *</Label><Input data-testid="student-registration-input" maxLength={20} value={form.registration || ""} onChange={(e) => set("registration", e.target.value)} /></div>
              <div><Label>Data de nascimento</Label><Input type="date" value={form.birth_date || ""} onChange={(e) => set("birth_date", e.target.value)} /></div>
              <div><Label>Turma</Label>
                <Select value={form.class_id || ""} onValueChange={(v) => set("class_id", v)}>
                  <SelectTrigger data-testid="student-class-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.grade}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Ano letivo</Label>
                <Select value={form.academic_year_id || ""} onValueChange={(v) => set("academic_year_id", v)}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.year}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Situação da matrícula</Label>
                <Select value={form.enrollment_status} onValueChange={(v) => set("enrollment_status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="MATRICULADO">MATRICULADO</SelectItem><SelectItem value="TRANSFERIDO">TRANSFERIDO</SelectItem><SelectItem value="INATIVO">INATIVO</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>URL da foto</Label><Input value={form.photo_url || ""} onChange={(e) => set("photo_url", e.target.value)} placeholder="https://..." /></div>
              <div className="sm:col-span-2"><Label>Endereço completo</Label><Textarea rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
            </div>

            <div className="rounded-lg bg-slate-50 p-3" data-testid="student-personal-section">
              <Label className="mb-2 block">Dados pessoais e documentos</Label>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2"><Label className="text-xs">Curso</Label><Input data-testid="student-course-input" placeholder="Ex.: Ensino Médio — Formação Geral" value={form.course || ""} onChange={(e) => set("course", e.target.value)} /></div>
                <div><Label className="text-xs">Nacionalidade</Label><Input data-testid="student-nationality-input" placeholder="Brasileira" value={form.nationality || ""} onChange={(e) => set("nationality", e.target.value)} /></div>
                <div className="grid grid-cols-[1fr_70px] gap-2">
                  <div><Label className="text-xs">Naturalidade (cidade)</Label><Input data-testid="student-birth-city-input" value={form.birth_city || ""} onChange={(e) => set("birth_city", e.target.value)} /></div>
                  <div><Label className="text-xs">UF</Label><Input data-testid="student-birth-state-input" maxLength={2} value={form.birth_state || ""} onChange={(e) => set("birth_state", e.target.value.toUpperCase())} /></div>
                </div>
                <div><Label className="text-xs">Nome do pai</Label><Input data-testid="student-father-input" value={form.father_name || ""} onChange={(e) => set("father_name", e.target.value)} /></div>
                <div><Label className="text-xs">Nome da mãe</Label><Input data-testid="student-mother-input" value={form.mother_name || ""} onChange={(e) => set("mother_name", e.target.value)} /></div>
                <div><Label className="text-xs">Identidade (RG)</Label><Input data-testid="student-rg-input" value={form.rg || ""} onChange={(e) => set("rg", e.target.value)} /></div>
                <div><Label className="text-xs">CPF</Label><Input data-testid="student-cpf-input" placeholder="000.000.000-00" value={form.cpf || ""} onChange={(e) => set("cpf", fmtCpf(e.target.value))} /></div>
                <div><Label className="text-xs">Órgão expedidor</Label><Input data-testid="student-rg-issuer-input" placeholder="Ex.: DETRAN/RJ" value={form.rg_issuer || ""} onChange={(e) => set("rg_issuer", e.target.value.toUpperCase())} /></div>
                <div><Label className="text-xs">Data de expedição</Label><Input data-testid="student-rg-date-input" type="date" value={form.rg_issue_date || ""} onChange={(e) => set("rg_issue_date", e.target.value)} /></div>
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3" data-testid="student-prior-studies-section">
              <div className="mb-2 flex items-center justify-between">
                <Label>Estudos anteriores (séries cursadas — histórico escolar)</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => set("prior_studies", [...(form.prior_studies || []), { grade: "", year: "", school: "", city: "", result: "APROVADO" }])} data-testid="prior-study-add">+ Série</Button>
              </div>
              {(form.prior_studies || []).map((p, i) => {
                const upd = (k, v) => set("prior_studies", form.prior_studies.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
                return (
                  <div key={i} className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-[80px_64px_1fr_110px_70px_96px]" data-testid={`prior-study-${i}`}>
                    <Input placeholder="Série" value={p.grade || ""} onChange={(e) => upd("grade", e.target.value)} />
                    <Input placeholder="Ano" value={p.year || ""} onChange={(e) => upd("year", e.target.value)} />
                    <Input placeholder="Estabelecimento" value={p.school || ""} onChange={(e) => upd("school", e.target.value)} />
                    <Input placeholder="Município/UF" value={p.city || ""} onChange={(e) => upd("city", e.target.value)} />
                    <Input placeholder="Resultado" value={p.result || ""} onChange={(e) => upd("result", e.target.value.toUpperCase())} />
                    <Input type="number" min={0} max={100} step="0.1" placeholder="Freq. %" value={p.attendance ?? ""} onChange={(e) => upd("attendance", e.target.value === "" ? null : Number(e.target.value))} data-testid={`prior-study-attendance-${i}`} />
                    <div className="col-span-2 flex gap-2 sm:col-span-6">
                      <Button type="button" size="sm" variant="outline" onClick={() => setGradesIdx(i)} data-testid={`prior-study-grades-${i}`}>Notas por disciplina{p.subjects?.length ? ` (${p.subjects.length})` : ""}</Button>
                      <Button type="button" size="sm" variant="ghost" className="text-rose-600" onClick={() => set("prior_studies", form.prior_studies.filter((_, j) => j !== i))}>Remover</Button>
                    </div>
                  </div>
                );
              })}
              <PriorGradesDialog open={gradesIdx !== null} onOpenChange={(o) => !o && setGradesIdx(null)} study={form.prior_studies?.[gradesIdx]}
                onSave={(subjects) => set("prior_studies", form.prior_studies.map((x, j) => (j === gradesIdx ? { ...x, subjects } : x)))} />
            </div>


            <div className="rounded-lg bg-slate-50 p-3">
              <div className="mb-2 flex items-center justify-between">
                <Label>Responsáveis</Label>
                <Button size="sm" variant="ghost" onClick={addG} data-testid="student-add-guardian"><UserPlus className="mr-1 h-4 w-4" /> Adicionar</Button>
              </div>
              {form.guardians?.map((g, i) => (
                <div key={i} className="mb-2 rounded-lg border border-slate-200 bg-white p-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div><Label className="text-xs">Nome</Label><Input value={g.name} onChange={(e) => setG(i, "name", e.target.value)} /></div>
                    <div><Label className="text-xs">Parentesco</Label><Input value={g.relationship} onChange={(e) => setG(i, "relationship", e.target.value)} placeholder="Mãe, Pai, Avó..." /></div>
                    <div><Label className="text-xs">Telefone</Label><Input value={g.phone} onChange={(e) => setG(i, "phone", e.target.value)} /></div>
                    <div><Label className="text-xs">WhatsApp</Label><Input value={g.whatsapp} onChange={(e) => setG(i, "whatsapp", e.target.value)} /></div>
                    <div className="sm:col-span-2"><Label className="text-xs">E-mail</Label><Input value={g.email} onChange={(e) => setG(i, "email", e.target.value)} /></div>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs">
                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={g.is_primary} onChange={(e) => setG(i, "is_primary", e.target.checked)} /> Principal</label>
                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={g.is_legal} onChange={(e) => setG(i, "is_legal", e.target.checked)} /> Responsável legal</label>
                    {form.guardians.length > 1 && <button onClick={() => removeG(i)} className="ml-auto text-red-500"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} disabled={saving || !form.full_name || !form.registration} data-testid="student-save-button" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={impOpen} onOpenChange={setImpOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="student-import-dialog">
          <DialogHeader><DialogTitle>Importar estudantes via planilha</DialogTitle></DialogHeader>

          {impStep === 1 && (
            <div className="grid gap-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <p className="mb-2 font-semibold text-slate-800">1. Baixe o modelo e preencha</p>
                <p>Colunas: Nome completo, Matrícula, Data de nascimento, Turma, Ano letivo, Situação da matrícula, Endereço completo, Curso, Nacionalidade, Naturalidade (cidade), UF de naturalidade, Nome do pai, Nome da mãe, Identidade (RG), CPF, Órgão expedidor, Data de expedição (DD/MM/AAAA), Nome do responsável, Parentesco, Telefone, WhatsApp, E-mail do responsável, Principal (sim/não), Responsável legal (sim/não).</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={downloadTemplate} data-testid="student-import-download-template"><FileDown className="mr-1.5 h-4 w-4" /> Baixar modelo (.xlsx)</Button>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="mb-2 font-semibold text-slate-800">2. Envie a planilha preenchida</p>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="student-import-file-label">
                  <Upload className="h-4 w-4" /> {impFile ? impFile.name : "Escolher arquivo (.xlsx ou .csv)"}
                  <input type="file" accept=".xlsx,.csv" className="hidden" data-testid="student-import-file-input" onChange={(e) => setImpFile(e.target.files?.[0] || null)} />
                </label>
              </div>
            </div>
          )}

          {impStep === 2 && (
            <div className="grid gap-3">
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700" data-testid="import-summary-total">Total: {impSummary?.total}</span>
                <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700" data-testid="import-summary-valid">Válidos: {impSummary?.valid}</span>
                <span className="rounded-full bg-rose-50 px-3 py-1 font-semibold text-rose-700" data-testid="import-summary-errors">Com erro: {impSummary?.errors}</span>
              </div>
              <Card className="max-h-80 overflow-auto border-slate-200" data-testid="import-preview-table">
                <Table>
                  <TableHeader><TableRow><TableHead>Linha</TableHead><TableHead>Nome</TableHead><TableHead>Matrícula</TableHead><TableHead>Turma</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {impReport.map((r) => (
                      <TableRow key={r.row}>
                        <TableCell className="text-slate-400">{r.row}</TableCell>
                        <TableCell className="font-medium text-slate-800">{r.full_name}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{r.registration}</TableCell>
                        <TableCell className="text-slate-600">{r.turma}</TableCell>
                        <TableCell>
                          {r.status === "ok"
                            ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> OK</span>
                            : <span className="inline-flex items-center gap-1 text-rose-600" title={r.errors.join("; ")}><XCircle className="h-4 w-4" /> {r.errors.join("; ")}</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
              {impSummary?.valid === 0 && <p className="text-sm text-rose-600">Nenhuma linha válida para importar. Corrija os erros e reenvie.</p>}
            </div>
          )}

          {impStep === 3 && (
            <div className="grid gap-3">
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700" data-testid="import-result-created">Criados: {impSummary?.created}</span>
                <span className="rounded-full bg-rose-50 px-3 py-1 font-semibold text-rose-700" data-testid="import-result-errors">Com erro: {impSummary?.errors}</span>
              </div>
              <Card className="max-h-80 overflow-auto border-slate-200" data-testid="import-results-table">
                <Table>
                  <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Matrícula</TableHead><TableHead>Turma</TableHead><TableHead>Resultado</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {impResults.map((r, i) => (
                      <TableRow key={i} data-testid={`import-result-row-${r.registration}`}>
                        <TableCell className="font-medium text-slate-800">{r.full_name}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{r.registration}</TableCell>
                        <TableCell className="text-slate-600">{r.class_name || "—"}</TableCell>
                        <TableCell>
                          {r.status === "created"
                            ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Criado</span>
                            : <span className="inline-flex items-center gap-1 text-rose-600"><XCircle className="h-4 w-4" /> {r.reason}</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setImpOpen(false)}>{impStep === 3 ? "Fechar" : "Cancelar"}</Button>
            {impStep === 1 && <Button onClick={doPreview} disabled={!impFile || impBusy} data-testid="student-import-preview-btn" className="bg-blue-700 hover:bg-blue-800">{impBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pré-visualizar"}</Button>}
            {impStep === 2 && <Button onClick={doConfirm} disabled={impBusy || !impSummary?.valid} data-testid="student-import-confirm-btn" className="bg-blue-700 hover:bg-blue-800">{impBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirmar importação (${impSummary?.valid || 0})`}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delStudent} onOpenChange={(o) => !o && setDelStudent(null)}>
        <DialogContent className="sm:max-w-md" data-testid="student-delete-dialog">
          <DialogHeader><DialogTitle>Excluir estudante</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Tem certeza que deseja excluir <strong className="text-slate-800">{delStudent?.full_name}</strong> (matrícula {delStudent?.registration})? Isso remove também ocorrências, notas, frequência, conquistas e certificados vinculados. Esta ação é permanente.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelStudent(null)}>Cancelar</Button>
            <Button onClick={doDeleteStudent} disabled={deletingStu} data-testid="student-delete-confirm-button" className="bg-rose-600 hover:bg-rose-700">{deletingStu ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
